/**
 * useTypingIndicator Hook
 * 
 * Manages typing indicator state for a conversation.
 * Features:
 * - WebSocket-only for typing (fast, low-cost, ephemeral)
 * - Send typing status when user types (debounced)
 * - Subscribe to other users' typing status via WebSocket
 * - Auto-clear after 3 seconds of no typing
 * - Clear immediately when message is sent
 * 
 * Note: Firestore typing was removed to reduce costs and latency.
 * WebSocket is sufficient for ephemeral typing indicators.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { TYPING_TIMEOUT } from '@/lib/firestore';

export function useTypingIndicator(conversationId, currentUserId, participants = []) {
  // Map of visibleKey (conversationId) -> Map of userId -> userName
  const [typingByConversation, setTypingByConversation] = useState({});
  
  // Refs for debouncing
  const typingTimeoutRef = useRef(null);
  const lastTypingRef = useRef(null);
  // Refs for WebSocket typing auto-clear timers (per user)
  const wsTypingTimersRef = useRef(new Map());

  // Listen for WebSocket typing indicators
  useEffect(() => {
    if (!conversationId) return;

    const handleWsTyping = (event) => {
      const data = event.detail;
      
      // Only handle typing for this conversation
      if (data.conversation_id !== conversationId) return;
      if (data.user_id === currentUserId) return;
      
      if (data.is_typing) {
        setTypingByConversation(prev => ({
          ...prev,
          [conversationId]: {
            ...prev[conversationId],
            [data.user_id]: data.user_name || 'Someone'
          }
        }));
        
        // Clear any existing timer for this user
        const timerKey = `${conversationId}_${data.user_id}`;
        if (wsTypingTimersRef.current.has(timerKey)) {
          clearTimeout(wsTypingTimersRef.current.get(timerKey));
        }
        
        // Auto-clear after TYPING_TIMEOUT
        const timer = setTimeout(() => {
          setTypingByConversation(prev => {
            const convTyping = { ...prev[conversationId] };
            delete convTyping[data.user_id];
            return { ...prev, [conversationId]: convTyping };
          });
          wsTypingTimersRef.current.delete(timerKey);
        }, TYPING_TIMEOUT);
        
        wsTypingTimersRef.current.set(timerKey, timer);
      } else {
        setTypingByConversation(prev => {
          const convTyping = { ...prev[conversationId] };
          delete convTyping[data.user_id];
          return { ...prev, [conversationId]: convTyping };
        });
        
        const timerKey = `${conversationId}_${data.user_id}`;
        if (wsTypingTimersRef.current.has(timerKey)) {
          clearTimeout(wsTypingTimersRef.current.get(timerKey));
          wsTypingTimersRef.current.delete(timerKey);
        }
      }
    };

    window.addEventListener('ws_typing_indicator', handleWsTyping);
    
    const timersRef = wsTypingTimersRef.current;
    
    return () => {
      window.removeEventListener('ws_typing_indicator', handleWsTyping);
      // Clear timers for this conversation
      timersRef.forEach((timer, key) => {
        if (key.startsWith(`${conversationId}_`)) {
          clearTimeout(timer);
          timersRef.delete(key);
        }
      });
    };
  }, [conversationId, currentUserId]);

  // Get typing users for current conversation (derived state, no setState needed)
  const typingUsers = useMemo(() => {
    const convTyping = typingByConversation[conversationId] || {};
    return Object.values(convTyping);
  }, [typingByConversation, conversationId]);

  // Send typing indicator (debounced) - WebSocket only
  const sendTypingIndicator = useCallback(() => {
    if (!conversationId || !currentUserId) return;

    // Don't send too frequently (max once per second)
    const now = Date.now();
    if (lastTypingRef.current && now - lastTypingRef.current < 1000) {
      return;
    }
    lastTypingRef.current = now;

    // Dispatch event for WebSocket sending (handled by App.js)
    window.dispatchEvent(new CustomEvent('send_typing', { 
      detail: { 
        conversationId, 
        participantIds: participants,
        isTyping: true 
      } 
    }));

    // Clear any existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set timeout to clear typing after TYPING_TIMEOUT
    typingTimeoutRef.current = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('send_typing', { 
        detail: { 
          conversationId, 
          participantIds: participants,
          isTyping: false 
        } 
      }));
    }, TYPING_TIMEOUT);
  }, [conversationId, currentUserId, participants]);

  // Clear typing indicator immediately (called when message is sent)
  const clearTypingIndicator = useCallback(() => {
    if (!currentUserId) return;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    window.dispatchEvent(new CustomEvent('send_typing', { 
      detail: { 
        conversationId, 
        participantIds: participants,
        isTyping: false 
      } 
    }));
    lastTypingRef.current = null;
  }, [currentUserId, conversationId, participants]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Format typing indicator text
  const getTypingText = useCallback(() => {
    if (typingUsers.length === 0) return null;
    
    if (typingUsers.length === 1) {
      return `${typingUsers[0]} is typing...`;
    }
    
    if (typingUsers.length === 2) {
      return `${typingUsers[0]} and ${typingUsers[1]} are typing...`;
    }
    
    return `${typingUsers[0]}, ${typingUsers[1]}, and ${typingUsers.length - 2} more are typing...`;
  }, [typingUsers]);

  return {
    typingUsers,
    isAnyoneTyping: typingUsers.length > 0,
    typingText: getTypingText(),
    sendTypingIndicator,
    clearTypingIndicator
  };
}
